import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";

export interface ComboOption {
  label: string;
  /** Muted extra text, also searchable (a city's partido). */
  hint?: string;
}

interface Props {
  id: string;
  label: string;
  options: ComboOption[];
  value: string;
  onChange: (value: string, option: ComboOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | null;
  hint?: string;
  /** Accept text that isn't in the list (small towns missing from the official list). */
  allowCustom?: boolean;
  emptyText: string;
  autoComplete?: string;
  maxSuggestions?: number;
}

/** Case- and accent-insensitive. */
const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/**
 * Single-choice select with type-ahead (ARIA combobox + listbox): typing filters the options
 * (words may match the label or the hint), ↑/↓ move, Enter or click picks, Escape closes. Without
 * allowCustom, leaving the field with text that is not an option restores the last valid value.
 */
export function Combobox({ id, label, options, value, onChange, placeholder, disabled, required, error, hint, allowCustom, emptyText, autoComplete = "off", maxSuggestions = 50 }: Props) {
  const listId = `${useId()}-list`;
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => setText(value), [value]);

  const matches = useMemo(() => {
    const words = fold(text).split(/\s+/).filter(Boolean);
    if (!words.length || fold(text) === fold(value)) return options.slice(0, maxSuggestions);
    const hay = (o: ComboOption) => fold(`${o.label} ${o.hint ?? ""}`);
    const starts = options.filter((o) => fold(o.label).startsWith(words.join(" ")));
    const rest = options.filter((o) => !starts.includes(o) && words.every((w) => hay(o).includes(w)));
    return [...starts, ...rest].slice(0, maxSuggestions);
  }, [text, value, options, maxSuggestions]);

  useEffect(() => setActive(0), [text]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const pick = (o: ComboOption) => {
    setText(o.label);
    setOpen(false);
    onChange(o.label, o);
  };

  const commit = () => {
    setOpen(false);
    if (text === value) return;
    const exact = options.find((o) => fold(o.label) === fold(text));
    if (exact) return pick(exact);
    if (allowCustom || !text.trim()) return onChange(text.trim(), null);
    setText(value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && open && matches[active]) {
      e.preventDefault();
      pick(matches[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-err` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className="field combobox">
      <label htmlFor={id}>{label}</label>
      <div className="autocomplete">
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          autoComplete={autoComplete}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={commit}
          onKeyDown={onKeyDown}
        />
        {open && !disabled && (
          <ul id={listId} ref={listRef} role="listbox" aria-label={label} className="autocomplete-list">
            {matches.length === 0 && <li className="autocomplete-empty">{emptyText}</li>}
            {matches.map((o, i) => (
              <li
                key={`${o.label}|${o.hint ?? ""}`}
                id={`${listId}-${i}`}
                data-index={i}
                role="option"
                aria-selected={i === active}
                className={i === active ? "is-active" : undefined}
                // mousedown, not click: fires before the input's blur.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
              >
                {o.label}
                {o.hint && <small className="muted"> {o.hint}</small>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {hint && (
        <span id={`${id}-hint`} className="field-hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={`${id}-err`} className="field-error">
          {error}
        </span>
      )}
    </div>
  );
}
